import React, { useState, useEffect } from 'react';
import { FaEye, FaEyeSlash, FaCamera, FaEdit, FaSave, FaTimes, FaLock, FaUser, FaEnvelope } from 'react-icons/fa';

import { getAuth, updateProfile, onAuthStateChanged, updatePassword, reauthenticateWithCredential, EmailAuthProvider } from 'firebase/auth';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { getFirestore, doc, getDoc, updateDoc } from 'firebase/firestore';

const ProfileSettings = () => {
  const auth = getAuth();
  const storage = getStorage();
  const db = getFirestore();

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    profilePicture: '',
  });
  const [isEditing, setIsEditing] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [isPasswordEditable, setIsPasswordEditable] = useState(false);
  const [error, setError] = useState('');
  const [showPopup, setShowPopup] = useState(false);
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [isCurrentPasswordVisible, setIsCurrentPasswordVisible] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');

  // Toggle visibility for passwords
  const togglePasswordVisibility = () => setIsPasswordVisible(!isPasswordVisible);
  const toggleCurrentPasswordVisibility = () => setIsCurrentPasswordVisible(!isCurrentPasswordVisible);

  // Function to populate user data from Firestore
  const populateUserData = async (user) => {
    if (user) {
      try {
        const userDocRef = doc(db, 'users', user.uid);
        const userDocSnap = await getDoc(userDocRef);

        if (userDocSnap.exists()) {
          const userData = userDocSnap.data();
          setFormData({
            firstName: userData.firstName || '',
            lastName: userData.lastName || '',
            email: user.email || '',
            password: '************',
            profilePicture: user.photoURL || '/profilepicture.png',
          });
        } else {
          console.log('No such document in Firestore!');
        }
      } catch (error) {
        console.error("Error fetching user data:", error);
        setError("Failed to load user data. Please try again.");
      }
    }
  };

  // Listen for authentication state changes and load user data
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        populateUserData(user);
      }
    });
    return () => unsubscribe();
  }, [auth]);

  // Handle form changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    setIsEditing(true);
  };

  // Function to display the popup for a few seconds
  const showSuccessPopup = () => {
    setShowPopup(true);
    setTimeout(() => {
      setShowPopup(false);
    }, 3000);
  };

  // Re-authenticate the user if required for password update
  const reauthenticateUser = async () => {
    const user = auth.currentUser;
    const credentials = EmailAuthProvider.credential(user.email, currentPassword);

    try {
      await reauthenticateWithCredential(user, credentials);
      console.log('User re-authenticated');
      return true;
    } catch (error) {
      console.error('Re-authentication failed:', error);
      setError('Re-authentication failed. Please enter your current password correctly.');
      return false;
    }
  };

  // Handle submit with password update
  const handleSubmit = async (e) => {
    e.preventDefault();

    const user = auth.currentUser;

    try {
      // Check if a new profile picture is selected
      let photoURL = formData.profilePicture;
      if (selectedFile) {
        const storageRef = ref(storage, `profilePictures/${user.uid}`);
        await uploadBytes(storageRef, selectedFile);
        photoURL = await getDownloadURL(storageRef);

        setFormData((prevData) => ({
          ...prevData,
          profilePicture: photoURL,
        }));
      }

      // Update the password if the password field is editable and contains a new value
      if (isPasswordEditable && formData.password !== '************') {
        const reauthenticated = await reauthenticateUser();
        if (!reauthenticated) return;

        await updatePassword(user, formData.password);
        console.log('Password updated');
      }

      // Update profile in Firebase Auth
      await updateProfile(user, {
        displayName: `${formData.firstName} ${formData.lastName}`,
        photoURL: photoURL,
      });

      // Update profile in Firestore
      const userDocRef = doc(db, 'users', user.uid);
      await updateDoc(userDocRef, {
        firstName: formData.firstName,
        lastName: formData.lastName,
        photoURL: photoURL,
      });
      
      // Update profile in Firestore (gameplay collection)
      const gameplayDocRef = doc(db, 'gameplay', user.uid);
      const gameplayDocSnap = await getDoc(gameplayDocRef);

      if (gameplayDocSnap.exists()) {
        await updateDoc(gameplayDocRef, {
          firstName: formData.firstName,
          lastName: formData.lastName,
          photoURL: photoURL,
        });
        console.log('Gameplay collection updated with new photoURL.');
      } else {
        console.log('Gameplay document not found for the user.');
      }

      // Show success popup
      showSuccessPopup();

      // Reset error if previously set
      setError('');
      console.log('User profile updated');
      setIsEditing(false);
    } catch (error) {
      console.error('Error updating profile:', error);
      setError('Failed to update profile. Please try again.');
    }
  };

  // Enable password editing
  const handlePasswordEdit = () => {
    setIsPasswordEditable(true);
    setFormData((prevData) => ({
      ...prevData,
      password: '', // Clear the placeholder to allow new password entry
    }));
  };

  // Handle profile picture change
  const handleProfilePicChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file); // Store the selected file for upload
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData((prevData) => ({
          ...prevData,
          profilePicture: reader.result, // Set the new image for preview
        }));
        setIsEditing(true);
      };
      reader.readAsDataURL(file);
    }
  };

  return (
    <div className="min-h-screen bg-[var(--background-color)] py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md mx-auto bg-[var(--surface-color)] rounded-xl shadow-lg overflow-hidden">
        <div className="relative h-32 bg-gradient-to-r from-[var(--primary-color)] to-[var(--interactive-color)]">
          <div className="absolute -bottom-16 left-1/2 transform -translate-x-1/2">
            <div className="relative group">
              <div className="w-32 h-32 rounded-full border-4 border-[var(--surface-color)] overflow-hidden bg-[var(--surface-color)]">
                <img
                  src={formData.profilePicture}
                  alt="Profile"
                  className="w-full h-full object-cover"
                  onError={(e) => {e.target.src = '/profilepicture.png'}}
                />
              </div>
              <div 
                className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50 rounded-full opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                onClick={() => document.getElementById('fileInput').click()}
              >
                <FaCamera className="text-white text-2xl" />
              </div>
              <input
                type="file"
                accept="image/*"
                onChange={handleProfilePicChange}
                id="fileInput"
                className="hidden"
              />
            </div>
          </div>
        </div>

        <div className="pt-20 px-6 pb-8">
          <h2 className="text-2xl font-bold text-center text-[var(--text-color)] mb-8">
            Profile Settings
          </h2>
          
          {error && (
            <div className="mb-6 p-3 bg-[var(--error-color)] bg-opacity-10 border border-[var(--error-color)] rounded-lg">
              <p className="text-[var(--error-color)]">{error}</p>
            </div>
          )}
          
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-4">
              {/* First Name */}
              <div>
                <label htmlFor="firstName" className="block text-sm font-medium text-[var(--text-color)]">
                  First Name
                </label>
                <div className="mt-1 relative rounded-md shadow-sm">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <FaUser className="text-[var(--secondary-color)]" />
                  </div>
                  <input
                    type="text"
                    id="firstName"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    required
                    className="pl-10 block w-full p-3 bg-[var(--background-color)] text-[var(--text-color)] border border-[var(--border-color)] rounded-lg focus:ring-2 focus:ring-[var(--interactive-color)] transition-colors"
                  />
                </div>
              </div>

              {/* Last Name */}
              <div>
                <label htmlFor="lastName" className="block text-sm font-medium text-[var(--text-color)]">
                  Last Name
                </label>
                <div className="mt-1 relative rounded-md shadow-sm">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <FaUser className="text-[var(--secondary-color)]" />
                  </div>
                  <input
                    type="text"
                    id="lastName"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    required
                    className="pl-10 block w-full p-3 bg-[var(--background-color)] text-[var(--text-color)] border border-[var(--border-color)] rounded-lg focus:ring-2 focus:ring-[var(--interactive-color)] transition-colors"
                  />
                </div>
              </div>

              {/* Email */}
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-[var(--text-color)]">
                  Email Address
                </label>
                <div className="mt-1 relative rounded-md shadow-sm">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <FaEnvelope className="text-[var(--secondary-color)]" />
                  </div>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    disabled
                    className="pl-10 block w-full p-3 bg-[var(--background-color)] bg-opacity-50 text-[var(--text-color)] text-opacity-70 border border-[var(--border-color)] rounded-lg cursor-not-allowed"
                  />
                </div>
              </div>

              {/* Password */}
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-[var(--text-color)]">
                  Password
                </label>
                <div className="mt-1 relative rounded-md shadow-sm">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <FaLock className="text-[var(--secondary-color)]" />
                  </div>
                  <input
                    type={isPasswordVisible ? "text" : "password"}
                    id="password"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    disabled={!isPasswordEditable}
                    className={`pl-10 pr-10 block w-full p-3 bg-[var(--background-color)] text-[var(--text-color)] border border-[var(--border-color)] rounded-lg ${!isPasswordEditable ? 'cursor-not-allowed bg-opacity-50 text-opacity-70' : 'focus:ring-2 focus:ring-[var(--interactive-color)]'} transition-colors`}
                  />
                  <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                    <button
                      type="button"
                      onClick={togglePasswordVisibility}
                      className="text-[var(--interactive-color)] hover:text-[var(--interactive-color)] focus:outline-none"
                      tabIndex="-1"
                    >
                      {isPasswordVisible ? <FaEyeSlash /> : <FaEye />}
                    </button>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={handlePasswordEdit}
                  className="mt-1 text-sm text-[var(--interactive-color)] hover:underline focus:outline-none focus:ring-2 focus:ring-[var(--interactive-color)] p-1 rounded"
                >
                  Change Password
                </button>
              </div>

              {/* Current Password (only shown when changing password) */}
              {isPasswordEditable && (
                <div>
                  <label htmlFor="currentPassword" className="block text-sm font-medium text-[var(--text-color)]">
                    Current Password
                  </label>
                  <div className="mt-1 relative rounded-md shadow-sm">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <FaLock className="text-[var(--secondary-color)]" />
                    </div>
                    <input
                      type={isCurrentPasswordVisible ? "text" : "password"}
                      id="currentPassword"
                      name="currentPassword"
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      required
                      className="pl-10 pr-10 block w-full p-3 bg-[var(--background-color)] text-[var(--text-color)] border border-[var(--border-color)] rounded-lg focus:ring-2 focus:ring-[var(--interactive-color)] transition-colors"
                    />
                    <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                      <button
                        type="button"
                        onClick={toggleCurrentPasswordVisibility}
                        className="text-[var(--interactive-color)] hover:text-[var(--interactive-color)] focus:outline-none"
                        tabIndex="-1"
                      >
                        {isCurrentPasswordVisible ? <FaEyeSlash /> : <FaEye />}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col space-y-3 sm:flex-row sm:space-y-0 sm:space-x-3">
              {isEditing && (
                <button
                  type="button"
                  onClick={() => window.location.reload()}
                  className="inline-flex justify-center items-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-[var(--text-color)] bg-[var(--surface-color-hover)] hover:bg-[var(--border-color)] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[var(--interactive-color)]"
                >
                  <FaTimes className="mr-2" /> Cancel
                </button>
              )}
              <button
                type="submit"
                className={`inline-flex justify-center items-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[var(--interactive-color)] hover:bg-opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[var(--interactive-color)] ${isEditing ? 'sm:flex-1' : 'w-full'}`}
              >
                <FaSave className="mr-2" /> Save Changes
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Success Popup */}
      {showPopup && (
        <div className="fixed bottom-4 right-4 bg-[var(--success-color)] text-white py-2 px-4 rounded-lg shadow-lg animate-dropdown">
          <p className="flex items-center">
            <FaSave className="mr-2" /> Profile updated successfully!
          </p>
        </div>
      )}
    </div>
  );
};

export default ProfileSettings;